import { useContext, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import {
  BuilderContext,
  type RegisteredComponent,
} from "@builder.io/sdk-react";

import {
  type RawGoalData,
  createGoalBanner,
} from "~/hooks/learning-center/transformers";
import { useFetchGoal } from "~/hooks/learning-center/use-fetch-lms";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import useLearningCenter from "~/hooks/use-learning-center";
import { useRootLayoutData } from "~/root";

import GoalBannerHeader from "./goal-banner-header";
import LevelCards from "./level-cards";
import messages from "./messages";
import type { GoalBannerEnrollment } from "./types";

/**
 * Port of hec-composable `goal-banner/goalBanner.jsx`. The LMS goal hero block
 * (HSA Learning Center): reads the goal reference from the page entry's
 * `BuilderContext.content.data.goal`, fetches the goal's level→module tree via
 * `useFetchGoal`, overlays the learner's SCAPI progress (`useLearningCenter`),
 * and renders the goal header card + the per-level cards ("Your Lessons" +
 * Level N with progress + CTA).
 *
 * `noWrap` block — registered as "GoalBanner" (the name live Builder content
 * references) and spreads `attributes` on its root element.
 *
 * Deltas vs composable:
 *  - Loyalty enrollment status comes from the root layout (`customerLoyalty`,
 *    parsed from persisted `c_acLoyaltyData`), not a live AnnexCloud client call.
 *    The per-level available-to-earn POINTS are not fetched client-side yet
 *    (AnnexCloud is server-consolidated — see `./types`); `GoalLevelCopy`
 *    degrades to non-numeric copy in the enrolled branch until a dedicated
 *    resource route lands.
 *  - `setActiveGoal` is fired once per goal (ref-guarded) instead of on every
 *    learningCenterData change.
 */
interface GoalBannerProps {
  attributes?: Record<string, unknown>;
}

const GoalBanner = ({ attributes }: GoalBannerProps) => {
  const intl = useIntl();
  const builderContext = useContext(BuilderContext);
  const goalRef = (
    builderContext?.content?.data as { goal?: { id?: string } } | undefined
  )?.goal;
  const goalContentId = goalRef?.id;

  const { data: goalEntry, isLoading } = useFetchGoal(goalContentId);
  const { learningCenterData, getGoalByID } = useLearningCenter();
  const { setActiveGoal } = useLearningCenterMutation();
  const { customerLoyalty } = useRootLayoutData();

  const goalData = goalEntry?.data as RawGoalData | undefined;
  const pageGoalId = goalData?.id;

  const bannerData = useMemo(() => {
    if (!goalData) return null;
    // getGoalByID is memoized on learningCenterData; list the value, not the fn.
    return createGoalBanner(goalData, getGoalByID(goalData.id) ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalEntry, learningCenterData]);

  const enrollment: GoalBannerEnrollment = {
    isEnrolled: customerLoyalty,
    isMaxEarned: null,
    maxEarned: null,
    loyaltyData: null,
  };

  // Mark this goal active on first view (composable parity) — guarded so it
  // fires once per goal id, not on every learningCenterData change.
  const activeSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pageGoalId || !learningCenterData) return;
    if (activeSyncedRef.current === pageGoalId) return;
    if (learningCenterData.activeGoal === pageGoalId) {
      activeSyncedRef.current = pageGoalId;
      return;
    }
    activeSyncedRef.current = pageGoalId;
    void setActiveGoal(pageGoalId);
  }, [pageGoalId, learningCenterData, setActiveGoal]);

  // No goal wired on the entry (e.g. the Builder editor canvas) — mirror the
  // levels block's hint rather than an empty band.
  if (!goalContentId) {
    return (
      <div {...(attributes ?? {})} data-testid="goal-banner-empty">
        {intl.formatMessage(messages.selectGoal)}
      </div>
    );
  }

  // Client fetch in flight — also the SSR/first-paint state (the fetcher runs
  // post-hydration). Reserve the band height to avoid a layout shift.
  if (isLoading || !bannerData) {
    return (
      <div {...(attributes ?? {})} data-testid="goal-banner-loading">
        <div className="w-full bg-indigo-01/40">
          <div className="container space-y-6 py-10" aria-busy="true">
            <div className="mx-auto h-40 max-w-screen-lg animate-pulse rounded-md bg-white/60" />
            <div className="flex flex-col gap-4 sm:flex-row">
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="h-44 flex-1 animate-pulse rounded-md bg-white/60"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...(attributes ?? {})} data-testid="goal-banner">
      <div
        className="w-full bg-cover bg-center"
        style={{
          backgroundImage: bannerData.image
            ? `url('${bannerData.image}')`
            : undefined,
        }}
      >
        <div className="w-full p-8">
          <GoalBannerHeader
            title={bannerData.title}
            description={bannerData.description}
          />
        </div>
        {/* Empty-level guard (SFCC-11724): only render the level cards when the
            goal resolved at least one level. With none, keep the hero header
            (still meaningful) and omit the cards row rather than render a lone
            "Your Lessons" instructions card with nothing beside it. */}
        {bannerData.levels.length > 0 && (
          <div className="w-full p-8">
            <LevelCards levels={bannerData.levels} enrollment={enrollment} />
          </div>
        )}
      </div>
    </div>
  );
};

export const GoalBannerDefinition = {
  component: GoalBanner,
  name: "GoalBanner",
  noWrap: true,
  canHaveChildren: false,
} satisfies RegisteredComponent;

export default GoalBanner;
